import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { User, Permissions, AppMetadata, Scope } from '../types';

export interface PortalAccess {
  admin: boolean;
  pos: boolean;
  ambassador: boolean;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  appMetadata: AppMetadata | null;
  loading: boolean;
  supabase: typeof supabase;
  isSuperAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  hasPermission: (feature: string, action: string) => boolean;
  getScope: () => Scope;
  getPortalAccess: () => PortalAccess;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const LEVEL_HIERARCHY: Record<string, number> = {
  off: 0,
  view: 1,
  contribute: 2,
  manage: 3,
};

const PORTAL_URLS: Record<string, string> = {
  DK: 'https://dk.app.cleanmybed.com',
  ZA: 'https://za.app.cleanmybed.com',
  HQ: 'https://global.app.cleanmybed.com',
};

function getCanonicalPortalUrl(): string {
  const code = (import.meta.env.VITE_COUNTRY_CODE as string | undefined)?.toUpperCase();
  return (code && PORTAL_URLS[code]) || window.location.origin;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [appMetadata, setAppMetadata] = useState<AppMetadata | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUserProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Failed to load user profile:', error.message);
      return;
    }

    setUser(data as User);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      setSession(currentSession);
      if (currentSession?.user) {
        setAppMetadata((currentSession.user.app_metadata as AppMetadata) ?? null);
        await loadUserProfile(currentSession.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession?.user) {
        setAppMetadata((newSession.user.app_metadata as AppMetadata) ?? null);
        loadUserProfile(newSession.user.id);
      } else {
        setUser(null);
        setAppMetadata(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadUserProfile]);

  // ------------------------------------------------------------------
  // Mechanism B: Silent JWT refresh when token_version changes
  // Polls country_config.token_version every 5 minutes.
  // On mismatch → refreshSession() to pick up new hook claims.
  // ------------------------------------------------------------------
  const lastRefreshedVersion = useRef<number | null>(null);

  useEffect(() => {
    if (!session) return;

    // Seed the ref from the current JWT so we don't refresh immediately
    const jwtVersion = (session.user.app_metadata as AppMetadata)?.token_version ?? null;
    if (lastRefreshedVersion.current === null && jwtVersion != null) {
      lastRefreshedVersion.current = jwtVersion;
    }

    async function checkTokenVersion() {
      try {
        const { data, error } = await supabase
          .from('country_config')
          .select('token_version')
          .limit(1)
          .single();

        if (error || !data) return;

        const dbVersion: number = data.token_version;
        const currentJwtVersion = lastRefreshedVersion.current;

        if (
          currentJwtVersion !== null &&
          dbVersion > currentJwtVersion
        ) {
          // eslint-disable-next-line no-console
          console.log(
            `Token version mismatch (JWT: ${currentJwtVersion}, DB: ${dbVersion}), refreshing session…`,
          );
          lastRefreshedVersion.current = dbVersion;
          await supabase.auth.refreshSession();
        } else if (currentJwtVersion === null) {
          // First poll — just record the DB value
          lastRefreshedVersion.current = dbVersion;
        }
      } catch {
        // Network error — will retry next interval
      }
    }

    // First check after 30s, then every 5 minutes
    const timeout = setTimeout(checkTokenVersion, 30_000);
    const interval = setInterval(checkTokenVersion, 5 * 60_000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [session]);

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }

  async function signOut() {
    localStorage.removeItem('sentinel_last_portal');
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setAppMetadata(null);
  }

  async function resetPasswordForEmail(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${getCanonicalPortalUrl()}/reset-password`,
    });
    if (error) throw error;
  }

  async function updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;

    const authUserId = session?.user?.id;
    if (authUserId) {
      const { error: userError } = await supabase
        .from('users')
        .update({ password_set_at: new Date().toISOString() })
        .eq('id', authUserId);
      if (userError) {
        console.error('Error updating password_set_at:', userError);
      }
    }
  }

  function hasPermission(
    feature: string,
    action: string,
  ): boolean {
    // Super admins bypass all permission checks
    if (appMetadata?.is_super_admin === true) return true;
    if (!user?.permissions) return false;
    const featurePerms = (user.permissions as Permissions)[feature];
    if (!featurePerms) return false;
    // Map action names to required permission levels
    const ACTION_TO_LEVEL: Record<string, string> = {
      view: 'view', list: 'view',
      create: 'contribute', edit: 'contribute', update: 'contribute',
      contribute: 'contribute', approve: 'manage',
      delete: 'manage', cancel: 'manage', manage: 'manage', process: 'manage',
      send: 'manage', whatsapp: 'manage', unlink_draft: 'manage',
      test_send: 'contribute',
    };
    // Unknown actions default to 'manage' — deny by default
    const requiredLevel = ACTION_TO_LEVEL[action] || 'manage';
    return (LEVEL_HIERARCHY[featurePerms.level] ?? 0) >= (LEVEL_HIERARCHY[requiredLevel] ?? 0);
  }

  function getScope(): Scope {
    return appMetadata?.scope ?? 'own';
  }

  function getPortalAccess(): PortalAccess {
    const perms = user?.permissions as Permissions | undefined;

    // Admin: super admin OR has any feature permission with level !== 'off'
    // (excluding meta keys and portal-access flags)
    const skipKeys = new Set(['_scope', 'ambassador_portal', 'pos_portal']);
    const hasAdminPerms = appMetadata?.is_super_admin === true || (
      perms != null && Object.entries(perms).some(
        ([key, val]) => !skipKeys.has(key) && val?.level && val.level !== 'off'
      )
    );

    // POS: is_pos_staff flag OR pos_portal permission
    const hasPosAccess = user?.is_pos_staff === true ||
      (perms?.pos_portal?.level != null && perms.pos_portal.level !== 'off');

    // Ambassador: is_ambassador flag OR ambassador_portal permission
    const hasAmbassadorAccess = user?.is_ambassador === true ||
      (perms?.ambassador_portal?.level != null && perms.ambassador_portal.level !== 'off');

    return {
      admin: hasAdminPerms,
      pos: hasPosAccess,
      ambassador: hasAmbassadorAccess,
    };
  }

  return (
    <AuthContext.Provider
      value={{ session, user, appMetadata, loading, supabase, isSuperAdmin: appMetadata?.is_super_admin === true, signIn, signOut, resetPasswordForEmail, updatePassword, hasPermission, getScope, getPortalAccess }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
